import { useState, useCallback } from "react";
import { Outlet } from "react-router-dom";
import { Sidebar, SIDEBAR_STORAGE_KEY, loadCollapsed } from "./Sidebar";
import { CommandBar } from "./CommandBar";
import { TerminalPanel } from "./TerminalPanel";
import { TopMetrics } from "./TopMetrics";
import { Rocket, LogOut } from "lucide-react";
import type { Agent } from "../lib/types";
import { getAuthToken } from "../lib/api";
import { TelegramIndicator } from "./NotificationFeed";

interface LayoutProps {
  wsConnected: boolean;
  onLogout?: () => void;
}

export function Layout({ wsConnected, onLogout }: LayoutProps) {
  const [collapsed, setCollapsed] = useState(loadCollapsed);
  const [terminalAgent, setTerminalAgent] = useState<Agent | null>(null);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      } catch {}
      return next;
    });
  }, []);

  function authHeaders(): Record<string, string> {
    const token = getAuthToken();
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  async function handleSendTask(projectId: string, agentId: string | null, instruction: string) {
    try {
      await fetch("/api/sorv/tasks", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ projectId, agentId, instruction }),
      });
    } catch {
      // silently fail
    }
  }

  async function handleSync(projectId: string) {
    try {
      await fetch(`/api/sorv/sync/${projectId}`, { method: "POST", headers: authHeaders() });
    } catch {
      // silently fail
    }
  }

  return (
    <div className="min-h-screen bg-surface-0 flex flex-col">
      <Sidebar wsConnected={wsConnected} collapsed={collapsed} onToggle={toggle} />

      {/* Top bar */}
      <div
        className="fixed top-0 right-0 z-20 h-12 bg-surface-1/80 backdrop-blur-sm border-b border-border flex items-center justify-between px-6 transition-[left] duration-200"
        style={{ left: collapsed ? "4.25rem" : "15rem" }}
      >
        <div className="flex items-center gap-3">
          <Rocket className="w-4 h-4 text-accent" />
          <span className="text-sm font-semibold text-gray-200">SORV Mission Control</span>
          {wsConnected && (
            <span className="flex items-center gap-1 text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-dot" />
              LIVE
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <TelegramIndicator />
          <TopMetrics />
          {onLogout && (
            <button
              onClick={onLogout}
              className="p-1.5 text-gray-500 hover:text-gray-300 transition-colors"
              title="Cerrar sesion"
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Main content */}
      <main
        className="flex-1 min-w-0 transition-[margin-left,width] duration-200 mt-12"
        style={{
          marginLeft: collapsed ? "4.25rem" : "15rem",
          width: collapsed ? "calc(100% - 4.25rem)" : "calc(100% - 15rem)",
        }}
      >
        <div className="p-6 max-w-full overflow-x-hidden">
          <Outlet context={{ setTerminalAgent }} />
        </div>
      </main>

      {/* Terminal Panel */}
      {terminalAgent && (
        <div
          className="fixed bottom-0 right-0 z-20 transition-[left] duration-200"
          style={{ left: collapsed ? "4.25rem" : "15rem" }}
        >
          <TerminalPanel agent={terminalAgent} onClose={() => setTerminalAgent(null)} />
        </div>
      )}

      {/* Command Bar */}
      <div
        className="fixed z-10 right-0 transition-[left] duration-200"
        style={{
          left: collapsed ? "4.25rem" : "15rem",
          bottom: terminalAgent ? "16rem" : 0,
        }}
      >
        <CommandBar onSendTask={handleSendTask} onSync={handleSync} />
      </div>
    </div>
  );
}
